import { DEFAULT_PER_PAGE } from "@/components/pagination";

import { formatToHumanReadable } from "@/lib/format-data";

import {
  ContactClock,
  ContactRecency,
  OpportunityFetchParams,
  OpportunityStatus,
  TouchpointChannel,
} from "@/types/opportunity";

import {
  booleanFilter,
  defineFilters,
  entitySetFilter,
  enumFilter,
  enumSetFilter,
  numberFilter,
  textFilter,
} from "@/features/table-filters/contract";

const statusOptions = Object.values(OpportunityStatus).map((status) => ({
  value: status,
  label: formatToHumanReadable(status),
}));

const sourceChannelOptions = Object.values(TouchpointChannel).map(
  (channel) => ({
    value: channel,
    label: formatToHumanReadable(channel),
  }),
);

const contactRecencyOptions = [
  { value: ContactRecency.NEVER_CONTACTED, label: "Never contacted" },
  { value: ContactRecency.CONTACTED_TODAY, label: "Contacted today" },
  { value: ContactRecency.COLD_3_DAYS, label: "No contact in 3 days" },
  { value: ContactRecency.COLD_7_DAYS, label: "No contact in 7 days" },
  { value: ContactRecency.COLD_14_DAYS, label: "No contact in 14 days" },
  { value: ContactRecency.COLD_30_DAYS, label: "No contact in 30 days" },
];

const contactClockOptions = [
  { value: ContactClock.LAST_ACTIVITY, label: "Anything, automations too" },
  { value: ContactClock.LAST_OUTREACH, label: "By a person" },
];

export const opportunityFilterDefinitions =
  defineFilters<OpportunityFetchParams>()([
    numberFilter<OpportunityFetchParams>({
      key: "page",
      label: "Page",
      presented: false,
      queryKey: "page",
    }),
    numberFilter<OpportunityFetchParams>({
      key: "perPage",
      label: "Per page",
      presented: false,
      queryKey: "per_page",
    }),
    textFilter<OpportunityFetchParams>({
      key: "sortBy",
      label: "Sort by",
      presented: false,
      queryKey: "sort_by",
    }),
    textFilter<OpportunityFetchParams>({
      key: "sortDirection",
      label: "Sort direction",
      presented: false,
      queryKey: "sort_direction",
    }),
    textFilter<OpportunityFetchParams>({
      key: "locationId",
      label: "Location",
      presented: false,
      queryKey: "location_id",
    }),
    entitySetFilter<OpportunityFetchParams>({
      key: "pipelineIds",
      label: "Pipeline",
      queryKey: "pipeline_id",
    }),
    entitySetFilter<OpportunityFetchParams>({
      key: "currentStageIds",
      label: "Stage",
      queryKey: "current_stage_id",
    }),
    entitySetFilter<OpportunityFetchParams>({
      key: "campaignIds",
      label: "Campaign",
      queryKey: "campaign_id",
    }),
    entitySetFilter<OpportunityFetchParams>({
      key: "assignedProviderIds",
      label: "Assigned To",
      queryKey: "assigned_provider_id",
    }),
    enumSetFilter<OpportunityFetchParams>({
      key: "statuses",
      label: "Status",
      queryKey: "status",
      options: statusOptions,
    }),
    enumSetFilter<OpportunityFetchParams>({
      key: "sourceChannels",
      label: "Source",
      queryKey: "source_channel",
      options: sourceChannelOptions,
    }),
    textFilter<OpportunityFetchParams>({
      key: "search",
      label: "Search",
      operator: "contains",
      queryKey: "search",
    }),
    booleanFilter<OpportunityFetchParams>({
      key: "isConverted",
      label: "Converted",
      presented: false,
      queryKey: "is_converted",
    }),
    enumFilter<OpportunityFetchParams>({
      key: "contactRecency",
      label: "Contact",
      control: "select",
      queryKey: "contact_recency",
      options: contactRecencyOptions,
    }),
    enumFilter<OpportunityFetchParams>({
      key: "contactClock",
      label: "Contact counts",
      control: "select",
      queryKey: "contact_clock",
      options: contactClockOptions,
    }),
  ]);

export const defaultOpportunityFilters: OpportunityFetchParams = {
  page: 1,
  perPage: DEFAULT_PER_PAGE,
  sortBy: "created_date",
  sortDirection: "desc",
};
